import React, { useState, useId } from "react";
import { Form, Badge, Image, OverlayTrigger, Popover } from "react-bootstrap";

const AVATAR_SIZE = 48;
const BIO_CLAMP_LINES = 3;
const READ_MORE_MIN_LENGTH = 140;

// AI verdict → Bootstrap colour-utility token. The card's left-border
// accent uses `border-start border-4 border-{success|warning|danger}`;
// the verdict badge uses the same token via Badge's `bg` prop.
const VERDICT_BOOTSTRAP_COLOR = {
  good: "success",
  ok: "warning",
  bad: "danger",
};

const VERDICT_BADGE_BG = VERDICT_BOOTSTRAP_COLOR;

const VERDICT_BADGE_TEXT = {
  good: "white",
  ok: "dark",
  bad: "white",
};

const VERDICT_ICON = {
  good: "fa-check",
  ok: "fa-circle-half-stroke",
  bad: "fa-xmark",
};

// Rounded-rect card rendering for a single candidate. Shared between
// the modal's card-mode results column and the form's inline
// RecipientSummary so admins get the same visual rhythm in both
// surfaces.
//
// Props:
//   candidate     — { id, name, profile_image_url, title, workplace,
//                     bio, is_stub, is_eligible, semantic_distance }
//   checked       — local checked state for this candidate's checkbox
//   onToggle      — fires on checkbox change
//   alreadyAdded  — render as already-on-list (faded, badge, disabled
//                   checkbox). Only the modal uses this.
//   isFocused     — render with focus highlight (modal detail-pane use)
//   onFocus       — fires when the card body is clicked (modal only)
//   showDistance  — show semantic_distance pill in the corner (modal)
//   verdict       — { verdict, reason, model, vetted_at } | null | undefined
//                   The card decorates with a coloured left border + a
//                   badge in the badge row + a hover popover showing
//                   the reason. Absence = not-vetted (no decoration).
//   stale         — the prompt-inputs changed since this verdict was
//                   generated. The badge is muted (lower opacity + a
//                   clock icon) and its popover notes the verdict
//                   predates the current story. Opacity is applied to
//                   the badge element only, not the card root (the card
//                   root already fades for already-added rows).
export default function RecipientCard({
  candidate,
  checked,
  onToggle,
  alreadyAdded = false,
  isFocused = false,
  onFocus,
  showDistance = false,
  verdict = null,
  stale = false,
}) {
  const distanceLabel =
    showDistance && candidate.semantic_distance != null ? candidate.semantic_distance.toFixed(2) : null;

  const verdictName = verdict?.verdict;
  const verdictColorToken = verdictName ? VERDICT_BOOTSTRAP_COLOR[verdictName] : null;
  // Left-border accent via Bootstrap utility classes; composes with the
  // existing `border rounded` on the card.
  const verdictBorderClass = verdictColorToken ? ` border-start border-4 border-${verdictColorToken}` : "";

  return (
    <div
      className={`border rounded p-2 d-flex flex-column h-100 position-relative bg-white${isFocused ? " border-primary" : ""}${verdictBorderClass}`}
      style={alreadyAdded ? { opacity: 0.6 } : undefined}
      onClick={(e) => {
        if (!onFocus) return;
        if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON") return;
        onFocus(candidate);
      }}
      onKeyDown={(e) => {
        if (!onFocus) return;
        if ((e.key === "Enter" || e.key === " ") && e.target.tagName !== "INPUT" && e.target.tagName !== "BUTTON") {
          e.preventDefault();
          onFocus(candidate);
        }
      }}
      role={onFocus ? "button" : undefined}
      tabIndex={onFocus ? 0 : undefined}
    >
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <Image
          roundedCircle
          src={candidate.profile_image_url}
          alt=""
          style={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
        <Form.Check
          type="checkbox"
          checked={alreadyAdded ? true : checked}
          disabled={alreadyAdded}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          aria-label={alreadyAdded ? `${candidate.name} (already on list)` : `Select ${candidate.name}`}
        />
      </div>

      <div className="fw-semibold text-truncate" title={candidate.name}>
        {candidate.name}
      </div>
      {(candidate.title || candidate.workplace) && (
        <div
          className="text-muted small text-truncate"
          title={[candidate.title, candidate.workplace].filter(Boolean).join(" · ")}
        >
          {[candidate.title, candidate.workplace].filter(Boolean).join(" · ")}
        </div>
      )}

      <div className="d-flex flex-wrap gap-1 mt-1">
        {alreadyAdded && (
          <Badge bg="secondary" pill style={{ fontSize: "0.65rem" }}>
            Added
          </Badge>
        )}
        {distanceLabel && (
          <Badge bg="light" text="dark" pill style={{ fontSize: "0.65rem" }} title="Semantic distance">
            {distanceLabel}
          </Badge>
        )}
        {verdictName && <VerdictBadge verdict={verdict} stale={stale} />}
        {candidate.is_stub && (
          <Badge bg="warning" text="dark">
            deleted
          </Badge>
        )}
        {candidate.is_eligible === false && !candidate.is_stub && (
          <Badge bg="warning" text="dark">
            not eligible
          </Badge>
        )}
      </div>

      {candidate.bio && <BioWithReadMore text={candidate.bio} />}
    </div>
  );
}

function BioWithReadMore({ text }) {
  const [expanded, setExpanded] = useState(false);
  const longEnough = text.length > READ_MORE_MIN_LENGTH;

  const clampStyle = expanded
    ? undefined
    : {
        display: "-webkit-box",
        WebkitLineClamp: BIO_CLAMP_LINES,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  return (
    <div className="small text-muted mt-2" style={{ whiteSpace: "pre-wrap" }}>
      <div style={clampStyle}>{text}</div>
      {longEnough && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mt-1"
          style={{ fontSize: "0.75rem" }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((s) => !s);
          }}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

// AI verdict pill with a hover popover that shows the AI's reason.
// Sits in the same badge row as the existing "Added"/"deleted"/"not
// eligible" pills so the visual rhythm is uniform. When `stale`, the
// pill is muted (lower opacity + a clock icon) to signal the verdict
// predates the current story inputs — advisory, not a gate.
function VerdictBadge({ verdict, stale = false }) {
  const name = verdict.verdict;
  const popoverId = useId();
  const popover = (
    <Popover id={popoverId}>
      <Popover.Header as="h6" className="text-capitalize small mb-0">
        AI verdict: {name}
        {stale && <span className="text-muted"> · out of date</span>}
      </Popover.Header>
      <Popover.Body className="small">
        {verdict.reason}
        {stale && (
          <div className="text-muted fst-italic mt-1">
            Vetted against an earlier version of this story — re-vet to refresh.
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={popover}>
      <Badge
        bg={VERDICT_BADGE_BG[name]}
        text={VERDICT_BADGE_TEXT[name]}
        pill
        style={{ fontSize: "0.65rem", cursor: "default", opacity: stale ? 0.65 : undefined }}
        role="status"
      >
        <i className={`fa-solid ${stale ? "fa-clock-rotate-left" : VERDICT_ICON[name]} me-1`} aria-hidden="true" />
        {name}
      </Badge>
    </OverlayTrigger>
  );
}

// Responsive grid of recipient cards. auto-fill at minmax(220px, 1fr)
// gives ~5 across at typical laptop width when the container has room;
// fewer on narrower viewports.
export function CardGrid({ children }) {
  return (
    <div
      className="p-2"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: "0.75rem",
      }}
    >
      {children}
    </div>
  );
}
